import {
    Award,
    GraduationCap,
    Globe2,
    Hospital,
    BookOpen,
    MapPin,
    Navigation,
} from "lucide-react";
import useReveal from "../hooks/useReveal";

const credentialGroups = [
    {
        icon: BookOpen,
        title: "Liderazgo Académico",
        items: [
            "Coordinador de la Asignatura de Cirugía · Escuela Militar de Medicina",
        ],
    },
    {
        icon: GraduationCap,
        title: "Formación de Excelencia",
        items: [
            "Especialista en Cirugía General con Mención Honorífica · Universidad del Ejército y Fuerza Aérea (Hospital Central Militar)",
            "Médico Cirujano · Universidad Anáhuac México Norte",
        ],
    },
    {
        icon: Globe2,
        title: "Entrenamiento Global de Vanguardia",
        items: [
            "Laparoscopia Avanzada · IRCAD (Francia / Estrasburgo)",
            "Laparoscopia Avanzada · Surgery Program de Stanford University",
            "Sistema quirúrgico Da Vinci Si · INTUITIVE",
        ],
    },
    {
        icon: Award,
        title: "Afiliaciones de Élite",
        items: [
            "Young Fellow of the American College of Surgeons (YFACS)",
            "Member · American Society for Parenteral and Enteral Nutrition (ASPEN)",
        ],
    },
];

const hospitals = [
    {
        name: "Hospital San Ángel Inn Satélite",
        kind: "Sede quirúrgica · Consultorio 1326",
        address: "Hospital San Ángel Inn, Naucalpan, Edo. Méx.",
        gmaps:
            "https://www.google.com/maps/search/?api=1&query=Hospital+San+%C3%81ngel+Inn+Sat%C3%A9lite",
        waze: "https://waze.com/ul?q=Hospital%20San%20%C3%81ngel%20Inn%20Sat%C3%A9lite&navigate=yes",
    },
    {
        name: "Hospital Star Médica Lomas Verdes",
        kind: "Sede quirúrgica · Consultorio 914",
        address: "Av. Lomas Verdes 1457, Naucalpan, Edo. Méx.",
        gmaps:
            "https://www.google.com/maps/search/?api=1&query=Star+M%C3%A9dica+Lomas+Verdes",
        waze: "https://waze.com/ul?q=Star%20M%C3%A9dica%20Lomas%20Verdes&navigate=yes",
    },
    {
        name: "Metabolik Center",
        kind: "Clínica metabólica · Nutribeauty Clinic",
        address: "Leona Vicario #20, San Melchor, Huixquilucan, Edo. Méx.",
        gmaps:
            "https://www.google.com/maps/search/?api=1&query=Leona+Vicario+20+San+Melchor+Huixquilucan",
        waze: "https://waze.com/ul?q=Leona%20Vicario%2020%20San%20Melchor%20Huixquilucan&navigate=yes",
    },
];

const QuienSoy = () => {
    const leftRef = useReveal();
    const rightRef = useReveal();
    const bottomRef = useReveal();

    return (
        <section
            id="quien-soy"
            data-testid="quien-soy-section"
            className="relative py-24 lg:py-32 bg-cream"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
                    {/* Copy column */}
                    <div ref={leftRef} className="reveal lg:col-span-7">
                        <div className="flex items-center gap-4 mb-6">
                            <span className="block w-16 h-px bg-gold" />
                            <span className="eyebrow">Quién soy</span>
                        </div>

                        <h2
                            data-testid="quien-soy-heading"
                            className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-navy max-w-2xl"
                        >
                            Formación de élite.
                            <br />
                            <span className="italic text-navy/85">Compromiso inquebrantable.</span>
                        </h2>

                        {/* Name + role line */}
                        <div className="mt-10 pl-4 border-l-2 border-gold/70">
                            <p className="font-display text-2xl sm:text-3xl text-navy leading-tight">
                                Dr. Erik E. Cárdenas Hernández
                            </p>
                            <p className="mt-2 text-[12px] sm:text-[13px] tracking-[0.16em] uppercase font-medium text-navy/65">
                                Cirujano General{" "}
                                <span className="text-gold mx-1">|</span> Especialista en
                                Laparoscopia Avanzada y Manejo Integral de la Obesidad
                            </p>
                        </div>

                        <div className="mt-10 space-y-6 text-ink/85 leading-relaxed max-w-2xl text-[15px] sm:text-base font-light">
                            <p>
                                Entender la cirugía moderna implica fusionar la máxima disciplina
                                clínica con la innovación tecnológica de vanguardia. Como
                                especialista, mi compromiso es ofrecer a cada paciente un camino
                                seguro, mínimamente invasivo y definitivo hacia su bienestar y
                                recuperación.
                            </p>
                            <p>
                                Mi trayectoria combina el rigor y la excelencia de la medicina
                                militar con una visión contemporánea y global de la cirugía de
                                alta especialidad.
                            </p>
                            <p className="text-navy font-normal italic font-display text-xl sm:text-2xl leading-snug pt-2">
                                “Cada paciente merece una atención técnicamente impecable y
                                humanamente cercana.”
                            </p>
                        </div>
                    </div>

                    {/* Headshot accent column (kept compact, optional) */}
                    <div ref={rightRef} className="reveal lg:col-span-5">
                        <div className="border border-navy/10 bg-white/60 backdrop-blur-sm">
                            <div className="px-6 lg:px-8 pt-8 pb-5 border-b border-navy/10 flex items-center justify-between">
                                <span className="font-display text-navy text-2xl leading-none">
                                    Credenciales clave
                                </span>
                                <span className="eyebrow !text-navy/40">Respaldo internacional</span>
                            </div>
                            <ul className="divide-y divide-navy/10">
                                {credentialGroups.map(({ icon: Icon, title, items }, i) => (
                                    <li
                                        key={title}
                                        data-testid={`credential-group-${i}`}
                                        className="px-6 lg:px-8 py-6 group hover:bg-cream-warm/50 transition-colors"
                                    >
                                        <div className="flex items-center gap-4 mb-3">
                                            <span className="shrink-0 w-10 h-10 border border-gold/50 flex items-center justify-center text-gold group-hover:bg-gold group-hover:text-navy transition-all duration-300">
                                                <Icon size={18} strokeWidth={1.5} />
                                            </span>
                                            <p className="font-display text-navy text-[1.15rem] leading-tight">
                                                {title}
                                            </p>
                                        </div>
                                        <ul className="ml-14 space-y-2">
                                            {items.map((it) => (
                                                <li
                                                    key={it}
                                                    className="text-[13px] leading-relaxed text-ink/75 flex gap-3"
                                                >
                                                    <span className="mt-2 w-1 h-1 rounded-full bg-gold shrink-0" />
                                                    <span>{it}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Centros de atención */}
                <div
                    ref={bottomRef}
                    className="reveal mt-16 lg:mt-20 bg-navy text-cream px-8 sm:px-12 lg:px-14 py-12 lg:py-14 border-t-2 border-gold relative"
                >
                    <div className="grid lg:grid-cols-12 gap-8 items-center">
                        <div className="lg:col-span-4">
                            <span className="eyebrow">Centros de atención</span>
                            <h3 className="mt-4 font-display text-3xl sm:text-4xl text-cream leading-tight">
                                Atención médica de{" "}
                                <span className="italic text-gold-soft">primer nivel.</span>
                            </h3>
                            <p className="mt-4 text-cream/70 text-[14px] sm:text-[15px] leading-relaxed font-light max-w-md">
                                Ofrezco atención con los más altos estándares de seguridad
                                intrahospitalaria en tres sedes seleccionadas por su excelencia
                                quirúrgica y hotelería médica.
                            </p>
                        </div>
                        <div className="lg:col-span-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
                            {hospitals.map((h, i) => (
                                <div
                                    key={h.name}
                                    data-testid={`hospital-card-${i}`}
                                    className="border border-cream/15 p-6 hover:border-gold/60 transition-colors duration-500 group flex flex-col"
                                >
                                    <div className="flex items-start gap-4">
                                        <span className="shrink-0 w-10 h-10 border border-gold/40 flex items-center justify-center text-gold group-hover:bg-gold group-hover:text-navy transition-all">
                                            <Hospital size={18} strokeWidth={1.5} />
                                        </span>
                                        <div>
                                            <p className="font-display text-cream text-xl leading-tight">
                                                {h.name}
                                            </p>
                                            <p className="mt-1 text-[10px] tracking-[0.28em] uppercase text-gold/80">
                                                {h.kind}
                                            </p>
                                            <p className="mt-3 text-[12px] text-cream/55 font-light leading-snug">
                                                {h.address}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="mt-5 pt-4 border-t border-cream/10 flex gap-2">
                                        <a
                                            data-testid={`hospital-${i}-gmaps`}
                                            href={h.gmaps}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 text-[10px] tracking-[0.22em] uppercase font-semibold border border-cream/20 text-cream/85 hover:border-gold hover:text-gold transition-colors"
                                        >
                                            <MapPin size={13} strokeWidth={1.6} />
                                            Google Maps
                                        </a>
                                        <a
                                            data-testid={`hospital-${i}-waze`}
                                            href={h.waze}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 text-[10px] tracking-[0.22em] uppercase font-semibold border border-cream/20 text-cream/85 hover:border-gold hover:text-gold transition-colors"
                                        >
                                            <Navigation size={13} strokeWidth={1.6} />
                                            Waze
                                        </a>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default QuienSoy;
